import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PartyService } from '../party.service';
import { Party } from '../party.model';
import { Customer } from 'src/app/customer/customer.model';
import { Company } from 'src/app/company/company.model';
import { CustomerService } from 'src/app/customer/customer.service';
import { PartyType } from '../party-type.enum';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { PartyTableComponent } from '../party-table/party-table.component';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PartyQuickCreateDialogComponent, PartyQuickCreateMode } from 'src/app/shared/party-quick-create/party-quick-create-dialog.component';
import { Driver } from 'src/app/fleet/driver/driver.model';
import { DriverService } from 'src/app/fleet/driver/driver.service';

const entityTypePropertyMap: { [key: string]: { data: string, total: string } } = {
    Customer: { data: 'customers', total: 'totalCustomers' },
    Driver: { data: 'drivers', total: 'totalDrivers' },
    Employee: { data: 'employees', total: 'totalEmployees' },
    Laborer: { data: 'laborers', total: 'totalLaborers' },
    Broker: { data: 'brokers', total: 'totalBrokers' },
    CustomsClearanceAgent: { data: 'customsClearanceAgents', total: 'totalCustomsClearanceAgents' }
};

@Component({
    selector: 'app-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToastModule,
        TabsModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        DialogModule,
        PartyTableComponent,
        PartyQuickCreateDialogComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        CustomerService
    ],
})
export class ContactsComponent implements OnInit {
    private readonly tabKeys = ['customers', 'drivers', 'employees', 'laborers', 'brokers', 'customs-agents'] as const;
    @Input() searchText = '';

    @ViewChild('customerTable') customerTable: PartyTableComponent;
    @ViewChild('driverTable') driverTable: PartyTableComponent;
    @ViewChild('employeeTable') employeeTable: PartyTableComponent;
    @ViewChild('laborerTable') laborerTable: PartyTableComponent;
    @ViewChild('brokerTable') brokerTable: PartyTableComponent;
    @ViewChild('agentTable') agentTable: PartyTableComponent;

    customers: Party[] = [];
    drivers: Party[] = [];
    employees: Party[] = [];
    laborers: Party[] = [];
    brokers: Party[] = [];
    customsClearanceAgents: Party[] = [];

    totalCustomers = 0;
    totalDrivers = 0;
    totalEmployees = 0;
    totalLaborers = 0;
    totalBrokers = 0;
    totalCustomsClearanceAgents = 0;

    owningCompanyId: number;
    isLoading = false;
    activeTabIndex = 0;
    searchTerms: Record<string, string> = {
        Customer: '',
        Driver: '',
        Employee: '',
        Laborer: '',
        Broker: '',
        CustomsClearanceAgent: ''
    };

    quickCreateVisible = false;
    quickCreateMode: PartyQuickCreateMode | null = null;
    quickCreateRecord: Company | Customer | Driver | null = null;
    quickCreateTitle: string | null = null;
    quickCreateSubmitLabel: string | null = null;
    quickCreateCompanyRequired = true;
    contactLookupVisible = false;
    contactLookupPhoneNumber = '';
    contactLookupSubmitting = false;
    contactLookupMode: PartyQuickCreateMode | null = null;
    contactLookupPartyType: PartyType | null = null;

    constructor(
        private partyService: PartyService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private customerService: CustomerService,
        private driverService: DriverService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        this.owningCompanyId = this.sessionStorageService.getCompany()?.id ?? 0;
    }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe(params => {
            this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
        });
    }

    get owningCompany(): Company | null {
        return this.sessionStorageService.getCompany() ?? null;
    }

    get countries() {
        return this.sessionStorageService.getCountryList() ?? [];
    }

    readonly contactDetailRoute = (party: Party): Array<string | number> | null => {
        switch (party?.id?.entityType) {
            case PartyType.Customer:
            case PartyType.Employee:
            case PartyType.Laborer:
            case PartyType.Broker:
            case PartyType.CustomsClearanceAgent:
                return party.id.entityId ? ['/customer', party.id.entityId] : null;
            case PartyType.Driver:
                return party.id.entityId ? ['/fleet/driver/view', party.id.entityId] : null;
            default:
                return null;
        }
    };

    createParty(partyType: string): void {
        const mode = this.modeForContactType(partyType);
        if (!mode) {
            this.messageService.add({ severity: 'warn', summary: 'Unavailable', detail: `${partyType} contacts cannot be created from this workspace.` });
            return;
        }

        if (this.shouldPromptContactLookup(mode)) {
            this.openContactLookup(mode);
            return;
        }

        this.quickCreateMode = mode;
        this.quickCreateRecord = mode === 'driver' ? new Driver() : new Customer();
        this.quickCreateTitle = this.dialogTitleForContactType(partyType, false);
        this.quickCreateSubmitLabel = 'Create';
        this.quickCreateCompanyRequired = mode !== 'employee' && mode !== 'driver';
        this.quickCreateVisible = true;
    }

    loadData(event: TableLazyLoadEvent, entityTypes: string[]): void {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.isLoading = false;
            return;
        }

        const page = (event.first ?? 0) / (event.rows ?? 10);
        const size = event.rows ?? 10;
        const searchText = this.getSearchText(entityTypes[0]);

        this.partyService.getParties(companyId, entityTypes, searchText, page, size)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (data) => {
                    const entityType = entityTypes[0];
                    const propertyNames = entityTypePropertyMap[entityType];
                    if (propertyNames) {
                        (this as any)[propertyNames.data] = data.content;
                        (this as any)[propertyNames.total] = data.totalElements;
                    }
                },
                error: (err) => {
                    console.error('Failed to load contact data:', err);
                    this.messageService.add({ severity: 'error', summary: 'Data Load Failed' });
                }
            });
    }

    reloadDataForActiveTab(): void {
        const tables = [
            this.customerTable,
            this.driverTable,
            this.employeeTable,
            this.laborerTable,
            this.brokerTable,
            this.agentTable
        ];
        tables[this.activeTabIndex]?.reset();
    }

    onTabChange(event: { index: number | string }): void {
        this.activeTabIndex = event.index as number;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    onSearch(): void {
        this.reloadDataForActiveTab();
    }

    clearSearch(entityType: string): void {
        this.searchTerms[entityType] = '';
        this.onSearch();
    }

    getSearchText(entityType: string): string {
        return (this.searchTerms[entityType] ?? this.searchText ?? '').trim();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Customers', value: this.totalCustomers, hint: 'Client contacts', tone: 'info' },
            { label: 'Drivers', value: this.totalDrivers, hint: 'Fleet operators', tone: 'success' },
            { label: 'Employees', value: this.totalEmployees, hint: 'Internal users', tone: 'warn' },
            { label: 'Brokers', value: this.totalBrokers + this.totalCustomsClearanceAgents, hint: 'Clearance contacts', tone: 'info' }
        ];
    }

    onQuickCreateCompleted(): void {
        this.quickCreateVisible = false;
        this.reloadDataForActiveTab();
    }

    onQuickCreateVisibilityChanged(visible: boolean): void {
        this.quickCreateVisible = visible;
        if (!visible) {
            this.resetQuickCreateState();
        }
    }

    onQuickCreateFailed(message: string): void {
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: message || 'Unable to save contact.' });
    }

    closeContactLookup(): void {
        this.contactLookupVisible = false;
        this.contactLookupPhoneNumber = '';
        this.contactLookupSubmitting = false;
        this.contactLookupMode = null;
        this.contactLookupPartyType = null;
    }

    submitContactLookup(): void {
        const lookupMode = this.contactLookupMode;
        const lookupPartyType = this.contactLookupPartyType;
        const phoneNumber = this.contactLookupPhoneNumber.trim();
        if (!lookupMode || !lookupPartyType || !phoneNumber || !this.owningCompany?.id) {
            return;
        }

        this.contactLookupSubmitting = true;
        this.partyService.linkContactByPhoneNumber(this.owningCompany.id, phoneNumber, lookupPartyType)
            .pipe(finalize(() => this.contactLookupSubmitting = false))
            .subscribe({
                next: resolution => {
                    if (resolution.status === 'NOT_FOUND') {
                        this.closeContactLookup();
                        const customer = new Customer();
                        customer.phoneNumber = phoneNumber;
                        this.quickCreateMode = lookupMode;
                        this.quickCreateRecord = customer;
                        this.quickCreateTitle = this.dialogTitleForContactType(lookupPartyType, false);
                        this.quickCreateSubmitLabel = 'Create';
                        this.quickCreateCompanyRequired = lookupMode !== 'employee' && lookupMode !== 'driver';
                        this.quickCreateVisible = true;
                        return;
                    }

                    const contactName = resolution.party?.entityName || this.contactLookupEntityLabel(lookupMode);
                    this.messageService.add({
                        severity: resolution.status === 'ALREADY_LINKED' ? 'info' : 'success',
                        summary: resolution.status === 'ALREADY_LINKED' ? 'Already Linked' : 'Linked',
                        detail: resolution.status === 'ALREADY_LINKED'
                            ? `${contactName} is already linked as ${this.contactLookupEntityLabel(lookupMode).toLowerCase()}.`
                            : `${contactName} was linked as ${this.contactLookupEntityLabel(lookupMode).toLowerCase()}.`
                    });
                    this.closeContactLookup();
                    this.reloadDataForActiveTab();
                },
                error: err => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Lookup Failed',
                        detail: err?.error?.message || err?.message || 'Unable to check the phone number.'
                    });
                }
            });
    }

    get contactLookupHeader(): string {
        return `Find Existing ${this.contactLookupEntityLabel(this.contactLookupMode)}`;
    }

    get contactLookupCopy(): string {
        const entityLabel = this.contactLookupEntityLabel(this.contactLookupMode).toLowerCase();
        return `Enter the phone number first. If the ${entityLabel} already exists, it will be linked immediately. If not, the create dialog will open with the phone number filled in.`;
    }

    editParty(party: Party): void {
        switch (party.id.entityType) {
            case PartyType.Customer:
            case PartyType.Employee:
            case PartyType.Laborer:
            case PartyType.Broker:
            case PartyType.CustomsClearanceAgent:
                this.loadCustomerLikeContact(party);
                break;
            case PartyType.Driver:
                this.loadDriver(party);
                break;
            default:
                this.messageService.add({ severity: 'info', summary: 'Info', detail: 'A detail page is not available for this contact type yet.' });
                break;
        }
    }

    private loadCustomerLikeContact(party: Party): void {
        this.isLoading = true;
        this.customerService.getCustomerById(party.id.entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response.data) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not fetch contact details.' });
                        return;
                    }
                    this.quickCreateMode = this.modeForContactType(party.id.entityType);
                    this.quickCreateRecord = response.data;
                    this.quickCreateTitle = this.dialogTitleForContactType(party.id.entityType, true);
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateCompanyRequired = party.id.entityType !== PartyType.Employee;
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to fetch contact: ' + (err.error?.message || err.message) });
                }
            });
    }

    private loadDriver(party: Party): void {
        this.isLoading = true;
        this.driverService.findDriverByFilter(undefined, party.id.entityId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    if (!response.data) {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not fetch driver details.' });
                        return;
                    }
                    this.quickCreateMode = 'driver';
                    this.quickCreateRecord = response.data;
                    this.quickCreateTitle = 'Edit Driver';
                    this.quickCreateSubmitLabel = 'Update';
                    this.quickCreateCompanyRequired = false;
                    this.quickCreateVisible = true;
                },
                error: (err) => {
                    this.messageService.add({ severity: 'error', summary: 'API Error', detail: 'Failed to fetch driver: ' + (err.error?.message || err.message) });
                }
            });
    }

    private modeForContactType(partyType: string): PartyQuickCreateMode | null {
        switch (partyType) {
            case PartyType.Customer:
                return 'customer';
            case PartyType.Driver:
                return 'driver';
            case PartyType.Employee:
                return 'employee';
            case PartyType.Laborer:
                return 'laborer';
            case PartyType.Broker:
                return 'brokerContact';
            case PartyType.CustomsClearanceAgent:
                return 'customsAgent';
            default:
                return null;
        }
    }

    private dialogTitleForContactType(partyType: string, editing: boolean): string {
        const action = editing ? 'Edit' : 'Create';
        switch (partyType) {
            case PartyType.Customer:
                return `${action} Customer`;
            case PartyType.Driver:
                return `${action} Driver`;
            case PartyType.Employee:
                return `${action} Employee`;
            case PartyType.Laborer:
                return `${action} Laborer`;
            case PartyType.Broker:
                return `${action} Broker Contact`;
            case PartyType.CustomsClearanceAgent:
                return `${action} Customs Agent`;
            default:
                return action;
        }
    }

    private resetQuickCreateState(): void {
        this.quickCreateMode = null;
        this.quickCreateRecord = null;
        this.quickCreateTitle = null;
        this.quickCreateSubmitLabel = null;
        this.quickCreateCompanyRequired = true;
    }

    private openContactLookup(mode: PartyQuickCreateMode): void {
        this.contactLookupMode = mode;
        this.contactLookupPartyType = this.partyTypeForLookupMode(mode);
        this.contactLookupPhoneNumber = '';
        this.contactLookupVisible = true;
    }

    private shouldPromptContactLookup(mode: PartyQuickCreateMode): boolean {
        return mode === 'customer'
            || mode === 'employee'
            || mode === 'laborer'
            || mode === 'brokerContact'
            || mode === 'customsAgent';
    }

    private partyTypeForLookupMode(mode: PartyQuickCreateMode): PartyType {
        switch (mode) {
            case 'employee':
                return PartyType.Employee;
            case 'laborer':
                return PartyType.Laborer;
            case 'brokerContact':
                return PartyType.Broker;
            case 'customsAgent':
                return PartyType.CustomsClearanceAgent;
            default:
                return PartyType.Customer;
        }
    }

    private contactLookupEntityLabel(mode: PartyQuickCreateMode | null): string {
        switch (mode) {
            case 'employee':
                return 'Employee';
            case 'laborer':
                return 'Laborer';
            case 'brokerContact':
                return 'Broker Contact';
            case 'customsAgent':
                return 'Customs Agent';
            default:
                return 'Customer';
        }
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }
}
